import { useState } from 'react';
import { Button, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { listHugimCourses } from '../../api/hugim';
import {
  RolePercentInput,
  rolePercentLabel,
  type CourseRolePercent,
} from '../settings/CoursePercentList';
import { PercentSumWarning } from '../settings/PercentSumWarning';

// Per-hug percentages for ONE staff member — the (staff, hug) level, the most
// specific in resolveHugPercent — one figure per role: what they get on a
// course where they are the primary, and where they are the second staff
// member. Controlled and staged: nothing is written until the drawer's save,
// which diffs this list against the loaded rows.
export function CoachHugPercentFields({
  value,
  onChange,
  staffName,
}: {
  value: CourseRolePercent[];
  onChange: (next: CourseRolePercent[]) => void;
  staffName: string;
}) {
  const coursesQ = useQuery({ queryKey: ['hugim-courses'], queryFn: listHugimCourses });

  const [hugSel, setHugSel] = useState<string | undefined>();
  const [primary, setPrimary] = useState<number | null>(null);
  const [secondary, setSecondary] = useState<number | null>(null);

  const courseName = (key: string) =>
    coursesQ.data?.find((c) => c.hug_key === key)?.hug_name ?? key;

  // This staff member's own hugim first — the list is box-wide and can be long.
  const teaches = (coaches: string[]) => coaches.some((c) => c.trim() === staffName.trim());
  const hugOptions = (coursesQ.data ?? [])
    .filter((c) => !value.some((v) => v.hug_key === c.hug_key))
    .sort((a, b) => Number(teaches(b.coaches)) - Number(teaches(a.coaches)))
    .map((c) => ({ value: c.hug_key, label: c.hug_name }));

  return (
    <div className="mt-4">
      <div className="text-label font-medium text-main-text mb-1">אחוז לפי חוג</div>
      <div className="text-small text-secondary-text mb-2">
        אחוז שונה לאיש צוות זה בחוגים מסוימים. גובר על כל שאר ההגדרות באותו חוג; בשאר החוגים חל
        האחוז שלמעלה.
      </div>
      <div className="flex items-end gap-2">
        <Select
          showSearch
          placeholder="בחר חוג"
          style={{ flex: 1 }}
          value={hugSel}
          onChange={setHugSel}
          options={hugOptions}
          optionFilterProp="label"
          loading={coursesQ.isLoading}
        />
        <RolePercentInput label="ראשי" value={primary} onChange={setPrimary} />
        <RolePercentInput label="משני" value={secondary} onChange={setSecondary} />
        <Button
          disabled={!hugSel || (primary == null && secondary == null)}
          onClick={() => {
            if (!hugSel || (primary == null && secondary == null)) return;
            onChange([...value, { hug_key: hugSel, primary, secondary }]);
            setHugSel(undefined);
            setPrimary(null);
            setSecondary(null);
          }}
        >
          הוסף
        </Button>
      </div>
      <PercentSumWarning primary={primary} secondary={secondary} />
      {value.length > 0 && (
        <div className="border rounded mt-2">
          {value.map((v) => (
            <div
              key={v.hug_key}
              className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-b-0"
            >
              <span className="text-main-text">{courseName(v.hug_key)}</span>
              <span className="flex items-center gap-3">
                <span className="text-main-text">{rolePercentLabel(v)}</span>
                <Button
                  size="small"
                  danger
                  type="text"
                  onClick={() => onChange(value.filter((x) => x.hug_key !== v.hug_key))}
                >
                  הסר
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
